import React, { useState } from 'react';

export default function SoftwareDevelopment() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans selection:bg-orange-500 selection:text-white">
      
      {/* ==========================================
          SECTION 1: HERO / VALUE PROPOSITION
         ========================================== */}
      <section className="relative overflow-hidden bg-white py-20 lg:py-28 border-b border-orange-100">
        {/* Background Decorative Accents */}
        <div className="absolute top-0 right-0 -z-10 w-96 h-96 bg-orange-100/60 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 left-0 -z-10 w-80 h-80 bg-orange-50/80 rounded-full blur-2xl transform -translate-x-1/3 translate-y-1/3" />

        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Text Column */}
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-600 font-semibold text-xs tracking-wider uppercase">
              <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
              Tailored Engineering
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight">
              Software Development <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-orange-400">
                Built For Your Unique Needs
              </span>
            </h1>

            <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
              Custom software solutions designed specifically to streamline your operations, drive growth, and solve your most complex business challenges.
            </p>

            <div className="flex flex-wrap items-center gap-4 pt-2">
              <a
                href="#contact-section"
                className="px-7 py-3.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold transition-all duration-200 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transform hover:-translate-y-0.5"
              >
                Start Your Project
              </a>
              <a
                href="#solutions-section"
                className="px-7 py-3.5 rounded-xl bg-white border border-slate-200 hover:border-orange-300 text-slate-700 hover:text-orange-600 font-semibold transition-all duration-200"
              >
                Explore Solutions
              </a>
            </div>

            {/* Quick Metrics */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-slate-100">
              <div>
                <p className="text-2xl font-bold text-orange-600">99.9%</p>
                <p className="text-xs text-slate-500 mt-1">Uptime Reliability</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-600">100%</p>
                <p className="text-xs text-slate-500 mt-1">Tailored Codebase</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-600">24/7</p>
                <p className="text-xs text-slate-500 mt-1">Maintenance Support</p>
              </div>
            </div>
          </div>

          {/* Right Visual Card */}
          <div className="lg:col-span-5 relative">
            <div className="relative bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-8 text-white shadow-2xl shadow-orange-500/20">
              <div className="flex items-center justify-between pb-6 border-b border-orange-400/40">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-white/30" />
                  <div className="w-3 h-3 rounded-full bg-white/30" />
                  <div className="w-3 h-3 rounded-full bg-white/30" />
                </div>
                <span className="text-xs font-mono bg-orange-700/50 px-2.5 py-1 rounded text-orange-100">
                  Custom Architecture
                </span>
              </div>

              <div className="py-8 space-y-4 font-mono text-sm">
                <div className="p-3 bg-white/10 rounded-lg backdrop-blur-sm">
                  <span className="text-orange-200">&gt;</span> Analyzing Business Workflows...
                </div>
                <div className="p-3 bg-white/10 rounded-lg backdrop-blur-sm">
                  <span className="text-orange-200">&gt;</span> Designing Scalable Database Architecture
                </div>
                <div className="p-3 bg-white/10 rounded-lg backdrop-blur-sm">
                  <span className="text-orange-200">&gt;</span> Building High-Performance Microservices
                </div>
              </div>

              <div className="pt-4 flex items-center justify-between text-xs text-orange-100">
                <span>Status: Optimized</span>
                <span>Security: Enterprise Grade</span>
              </div>
            </div>
          </div>

        </div>
      </section>


      {/* ==========================================
          SECTION 2: CUSTOM SOLUTIONS & CAPABILITIES
         ========================================== */}
      <section id="solutions-section" className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-3">
            <span className="text-orange-600 font-bold text-xs uppercase tracking-widest">
              Core Expertise
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
              Custom Engineering Solutions
            </h2>
            <p className="text-slate-600 text-base">
              We build end-to-end applications designed to fit seamlessly into your existing operations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* Card 1 */}
            <div className="bg-white p-8 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:border-orange-300 transition-all duration-300 group">
              <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-500 group-hover:text-white transition-colors duration-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-orange-600 transition-colors">
                Custom Web Applications
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Responsive, high-speed web apps tailored to handle high-throughput user interactions and complex business logic.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white p-8 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:border-orange-300 transition-all duration-300 group">
              <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-500 group-hover:text-white transition-colors duration-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-orange-600 transition-colors">
                Enterprise ERP & CRM
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Consolidate your business processes into unified software platforms designed for maximum team efficiency.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white p-8 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:border-orange-300 transition-all duration-300 group">
              <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-500 group-hover:text-white transition-colors duration-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 001-9.999 5.002 5.002 0 00-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-orange-600 transition-colors">
                Cloud Architecture & APIs
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Secure cloud migration, microservices engineering, and seamless third-party API integrations.
              </p>
            </div>

            {/* Card 4 */}
            <div className="bg-white p-8 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:border-orange-300 transition-all duration-300 group">
              <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-500 group-hover:text-white transition-colors duration-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-orange-600 transition-colors">
                Cross-Platform Mobile Apps
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Native-performing iOS and Android applications built on robust modern frameworks.
              </p>
            </div>

            {/* Card 5 */}
            <div className="bg-white p-8 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:border-orange-300 transition-all duration-300 group">
              <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-500 group-hover:text-white transition-colors duration-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-orange-600 transition-colors">
                Legacy App Modernization
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Upgrade outdated codebases and legacy systems without disrupting daily business operations.
              </p>
            </div>

            {/* Card 6 */}
            <div className="bg-white p-8 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:border-orange-300 transition-all duration-300 group">
              <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mb-6 group-hover:bg-orange-500 group-hover:text-white transition-colors duration-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-orange-600 transition-colors">
                Quality Assurance & Testing
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Automated security audits, performance testing, and bug tracking for bulletproof deployment.
              </p>
            </div>

          </div>
        </div>
      </section>


      {/* ==========================================
          SECTION 3: PROCESS & GET STARTED CTA
         ========================================== */}
      <section id="contact-section" className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-gradient-to-br from-orange-500 via-orange-600 to-amber-600 rounded-3xl p-8 lg:p-14 text-white shadow-2xl relative overflow-hidden">
            
            {/* Background pattern */}
            <div className="absolute top-0 right-0 -translate-y-12 translate-x-12 w-64 h-64 bg-white/10 rounded-full blur-2xl pointer-events-none" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center relative z-10">
              
              {/* Left Column: Process Steps */}
              <div className="lg:col-span-6 space-y-6">
                <span className="bg-white/20 text-white font-semibold text-xs px-3 py-1 rounded-full uppercase tracking-wider">
                  How We Work
                </span>
                <h2 className="text-3xl sm:text-4xl font-extrabold leading-tight">
                  Ready to Build Your Custom Software Solution?
                </h2>
                <p className="text-orange-100 text-base leading-relaxed">
                  Partner with an engineering team focused on delivering high quality, modular code tailored directly to your business goals.
                </p>

                {/* Workflow Timeline */}
                <div className="space-y-4 pt-4">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-white text-orange-600 font-bold flex items-center justify-center shrink-0">
                      1
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">Discovery & Strategy</h4>
                      <p className="text-xs text-orange-100">We analyze your requirements and define system architecture.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-white text-orange-600 font-bold flex items-center justify-center shrink-0">
                      2
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">Agile Development</h4>
                      <p className="text-xs text-orange-100">Iterative sprint builds with continuous feedback & updates.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-white text-orange-600 font-bold flex items-center justify-center shrink-0">
                      3
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">Deployment & Scale</h4>
                      <p className="text-xs text-orange-100">Rigorous QA, continuous integration, and long-term support.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Contact Form */}
              <div className="lg:col-span-6">
                <div className="bg-white text-slate-800 rounded-2xl p-6 sm:p-8 shadow-xl">
                  <h3 className="text-2xl font-bold text-slate-900 mb-1">
                    Get in Touch
                  </h3>
                  <p className="text-slate-500 text-sm mb-6">
                    Fill in your details for a free project estimate and consultation.
                  </p>

                  {submitted ? (
                    <div className="bg-orange-50 border border-orange-200 text-orange-800 p-6 rounded-xl text-center space-y-2">
                      <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto text-xl font-bold">
                        ✓
                      </div>
                      <h4 className="font-bold text-lg">Message Received!</h4>
                      <p className="text-sm text-slate-600">
                        Thanks for reaching out. Our software team will respond within 24 hours.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                          Full Name
                        </label>
                        <input
                          type="text"
                          required
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:border-orange-500 focus:bg-white transition-colors text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                          Work Email
                        </label>
                        <input
                          type="email"
                          required
                          placeholder="john@company.com"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:border-orange-500 focus:bg-white transition-colors text-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                          Project Details
                        </label>
                        <textarea
                          rows="3"
                          required
                          placeholder="Briefly describe your software requirements..."
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:border-orange-500 focus:bg-white transition-colors text-sm"
                        ></textarea>
                      </div>

                      <button
                        type="submit"
                        className="w-full py-3.5 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg transition-colors shadow-lg shadow-orange-500/30"
                      >
                        Request Consultation
                      </button>
                    </form>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

    </div>
  );
}